import React, { useState, createContext } from 'react'
import { FormattedMessage } from 'react-intl'
import { Dialog } from '@mui/material'

import AreYouSureDialog from './types/areyousure'
import AddRecipientDialog from './types/addrecipient'
import EditRecipientDialog from './types/editrecipient'

type dialogTypes =
  | 'areyousure'
  | 'addrecipient'
  | 'editrecipient'
  | undefined

export interface IDialogContext {
  toggle: (
    type: dialogTypes,
    onOkCallback?: CallableFunction,
    customProps?: CustomDialogProps
  ) => void
}

const DialogContext = createContext<IDialogContext>({
  toggle: (
    type: dialogTypes,
    onOkCallback?: CallableFunction,
    customProps?: CustomDialogProps
  ) => {},
})

export type DialogProviderProps = AT.IReactPropsDefaults
export type CustomDialogProps = {
  recipientid?: number
  name?: string
  email?: string
  variant?: 'shoppingcard' | 'contact' | 'feedback' | 'feedbackNotification' | 'ministry'
}

export function DialogProvider({ children }: DialogProviderProps) {
  const [open, setOpen] = useState<boolean>(false)
  const [type, setType] = useState<dialogTypes>('areyousure')
  const [onOk, setOnOk] = useState<CallableFunction | null>(null)
  const [customProps, setCustomProps] = useState<CustomDialogProps>({})

  function toggle(
    dialogtype: dialogTypes,
    onOkCallback?: CallableFunction,
    customProps?: CustomDialogProps
  ) {
    if (type !== dialogtype) setType(dialogtype)
    if (onOkCallback && !open) setOnOk(() => onOkCallback) // set onOk only if dialog is not open yet
    if (customProps && !open) setCustomProps(customProps)
    if (open) {
      setOnOk(null) // set onOk to null if dialog is about to close
      setCustomProps({})
    }
    setOpen(!open)
  }

  // useCallback because of heavy rerender. Only if type changes this function should be called
  function renderDialog() {
    if (!open) return
    const dialogprops = {
      setOpen: setOpen,
      onOk: onOk,
    }
    switch (type) {
      case 'addrecipient':
        if (!customProps.variant) {
          console.error(
            'Error DIALOG: Could not open "editrecipient" dialog. Data is missing in customProps of dialog.'
          )
          return (
            <div>
              <FormattedMessage id="snackbar.deverror" />
            </div>
          )
        }
        return (
          <AddRecipientDialog variant={customProps.variant} {...dialogprops} />
        )
      case 'editrecipient':
        if (
          !customProps.recipientid ||
          !customProps.name ||
          !customProps.email ||
          !customProps.variant
        ) {
          console.error(
            'Error DIALOG: Could not open "editrecipient" dialog. Data is missing in customProps of dialog.'
          )
          return (
            <div>
              <FormattedMessage id="snackbar.deverror" />
            </div>
          )
        }
        return (
          <EditRecipientDialog
            {...dialogprops}
            variant={customProps.variant}
            recipientid={customProps.recipientid}
            name={customProps.name}
            email={customProps.email}
          />
        )
      case 'areyousure':
        return <AreYouSureDialog {...dialogprops} />
      default:
        return <div>...</div>
    }
  }

  return (
    <DialogContext.Provider value={{ toggle: toggle }}>
      {children}
      <Dialog open={open} onClose={() => setOpen(false)}>
        {renderDialog()}
      </Dialog>
    </DialogContext.Provider>
  )
}

export default DialogContext
